import {AfterViewInit, Component, ElementRef, ViewChild, ViewEncapsulation} from '@angular/core';
import {CardComponent} from "../../components/ui/card/card.component";
import {LoaderComponent} from "../../components/ui/loader/loader.component";
import {PointCloudService} from "../../services/point-cloud.service";
import {firstValueFrom, map} from "rxjs";
import {PointCloud} from "../../models/point-cloud";
import {ActivatedRoute} from "@angular/router";
import * as THREE from 'three';
import {WebGLRenderer} from 'three';
import {OrbitControls} from './OrbitControls';
import {PLYLoader} from './PLYLoader';
import {ButtonComponent} from "../../components/ui/button/button.component";
import {TranslateModule} from "@ngx-translate/core";
import {AsyncPipe, DecimalPipe, NgIf} from "@angular/common";
import {PointCloudTag} from "../../models/point-cloud-tag";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {InputComponent} from "../../components/ui/input/input.component";

enum ModifyingStep {
  None = 'none',
  pointOne = 'reference-point-1',
  pointTwo = 'reference-point-2',
  pointThree = 'reference-point-3',
  Error = 'error',
  Success = 'success',
}

@Component({
  selector: 'app-point-cloud-detail-viewer',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CardComponent,
    LoaderComponent,
    ButtonComponent,
    TranslateModule,
    NgIf,
    AsyncPipe,
    DecimalPipe,
    ReactiveFormsModule,
    InputComponent
  ],
  templateUrl: './point-cloud-detail-viewer.component.html',
  styleUrl: './point-cloud-detail-viewer.component.scss'
})
export class PointCloudDetailViewerComponent implements AfterViewInit {
  pointCloud$ = this.route.parent!.data.pipe(map(data => data['pointCloud'] as PointCloud));
  standalone$ = this.route.parent!.data.pipe(map(data => data['standalone'] as boolean));
  loading = true;
  modifying = ModifyingStep.None
  @ViewChild('viewer') viewer?: ElementRef<HTMLDivElement>;
  private renderer?: WebGLRenderer;
  private camera?: THREE.PerspectiveCamera;
  private scene?: THREE.Scene;
  private meshTranslation = new THREE.Vector3();
  public pointOne: THREE.Vector3 | null = null;
  public pointTwo: THREE.Vector3 | null = null;
  public pointThree: THREE.Vector3 | null = null;
  public loadingModification = false;
  public form = new FormGroup({
    pointOne: new FormGroup({
      newX: new FormControl(0, [Validators.required]),
      newY: new FormControl(0, [Validators.required]),
      newZ: new FormControl(0, [Validators.required]),
    }),
    pointTwo: new FormGroup({
      newX: new FormControl(0, [Validators.required]),
      newY: new FormControl(0, [Validators.required]),
      newZ: new FormControl(0, [Validators.required]),
    }),
    pointThree: new FormGroup({
      newX: new FormControl(0, [Validators.required]),
      newY: new FormControl(0, [Validators.required]),
      newZ: new FormControl(0, [Validators.required]),
    }),
  });
  private controls: OrbitControls | null = null;

  constructor(private pointCloudService: PointCloudService, private route: ActivatedRoute) {
  }

  ngAfterViewInit() {
    this.load();
  }

  async load() {
    if (!this.viewer) {
      return;
    }

    const width = this.viewer.nativeElement.clientWidth;
    const height = this.viewer.nativeElement.clientHeight;


    this.camera = new THREE.PerspectiveCamera(70, width / height, 0.01, 10_000);
    this.camera.position.z = 1;

    this.scene = new THREE.Scene();

    this.renderer = new THREE.WebGLRenderer({antialias: true});
    this.renderer.setSize(width, height);

    // white background
    this.renderer.setClearColor(0xffffff, 1);

    this.viewer.nativeElement.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;

    const pointCloud = await firstValueFrom(this.pointCloud$);
    this.loadModel(pointCloud);

    this.viewer.nativeElement.addEventListener('click', (event) => {
      if (this.modifying) {
        this.handleMouseDown(event);
      }
    });

    const render = () => {
      this.renderer?.render(this.scene!, this.camera!)
    };

    const animate = () => {
      requestAnimationFrame(animate)

      this.controls!.update();
      this.updateLabels(pointCloud.tags);

      render()
    };

    animate()
  }

  modifyCoordinates() {
    this.modifying = ModifyingStep.pointOne;
  }

  addTagLabels(tags: PointCloudTag[]) {
    const viewerElement = this.viewer!.nativeElement;

    for (const tag of tags) {
      const tagElement = document.createElement('div');
      tagElement.className = 'tag-label';
      tagElement.textContent = tag.tag.name;
      tagElement.style.position = 'absolute';
      viewerElement.appendChild(tagElement);

      // Convert color (hex) to rgba
      const color = tag.tag.color;
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      tagElement.style.backgroundColor = `rgba(${r}, ${g}, ${b}, 0.3)`;
      tagElement.style.color = color;

      tag.element = tagElement;
    }
  }

  updateLabels(tags: PointCloudTag[]) {
    for (const tag of tags) {
      const canvas = this.renderer!.domElement;
      const tagVector = new THREE.Vector3(tag.x, tag.y, tag.z);
      const vector = tagVector.clone().add(this.meshTranslation).project(this.camera!);
      const x = (vector.x * 0.5 + 0.5) * canvas.clientWidth;
      const y = (vector.y * -0.5 + 0.5) * canvas.clientHeight;

      const tagElement = tag.element;

      if (tagElement) {
        tagElement.style.left = `${x}px`;
        tagElement.style.top = `${y}px`;
      }
    }
  }

  handleMouseDown(event: MouseEvent) {
    const nearestPoint = this.findNearestPoint(event);

    if (!nearestPoint) {
      return;
    }


    let groupName: string = '';
    if (this.modifying === ModifyingStep.pointOne) {
      this.pointOne = nearestPoint;
      groupName = 'pointOne';
    } else if (this.modifying === ModifyingStep.pointTwo) {
      this.pointTwo = nearestPoint;
      groupName = 'pointTwo';
    } else if (this.modifying === ModifyingStep.pointThree) {
      this.pointThree = nearestPoint;
      groupName = 'pointThree';
    }

    if (!groupName) {
      return;
    }

    const group = this.form.get(groupName) as FormGroup;
    if (group.get('newX')!.value === 0 && group.get('newY')!.value === 0 && group.get('newZ')!.value === 0) {
      group.get('newX')!.setValue(nearestPoint.x);
      group.get('newY')!.setValue(nearestPoint.y);
      group.get('newZ')!.setValue(nearestPoint.z);
    }
  }

  findNearestPoint(event: MouseEvent): THREE.Vector3 | null {
    // Find the nearest point cloud point to the mouse position
    const canvas = this.renderer!.domElement;
    const rect = canvas.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    const mouse = new THREE.Vector2(x, y);

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.camera!);

    const mesh = this.scene!.children[0] as THREE.Points;
    const intersects = raycaster.intersectObject(mesh);

    if (intersects.length > 0) {
      return intersects[0].point.clone().sub(this.meshTranslation);
    }

    return null;
  }

  get isModifying() {
    return this.modifying !== ModifyingStep.None;
  }

  get isSteppointOne() {
    return this.modifying === ModifyingStep.pointOne;
  }

  get isSteppointTwo() {
    return this.modifying === ModifyingStep.pointTwo;
  }

  get isSteppointThree() {
    return this.modifying === ModifyingStep.pointThree;
  }

  nextStep() {
    if (this.modifying === ModifyingStep.pointOne) {
      this.modifying = ModifyingStep.pointTwo;
    } else if (this.modifying === ModifyingStep.pointTwo) {
      this.modifying = ModifyingStep.pointThree;
    }
  }

  async confirm() {
    if (!this.pointOne || !this.pointTwo || !this.pointThree || this.loadingModification || !this.form.valid) {
      return;
    }

    try {
      this.loadingModification = true;

      const pointCloud = await firstValueFrom(this.pointCloud$);
      await firstValueFrom(this.pointCloudService.modifyCoordinates(pointCloud, {
        pointOneOriginalX: this.pointOne.x,
        pointOneOriginalY: this.pointOne.y,
        pointOneOriginalZ: this.pointOne.z,

        pointOneModifiedX: this.form.get('pointOne')!.get('newX')!.value!,
        pointOneModifiedY: this.form.get('pointOne')!.get('newY')!.value!,
        pointOneModifiedZ: this.form.get('pointOne')!.get('newZ')!.value!,

        pointTwoOriginalX: this.pointTwo.x,
        pointTwoOriginalY: this.pointTwo.y,
        pointTwoOriginalZ: this.pointTwo.z,

        pointTwoModifiedX: this.form.get('pointTwo')!.get('newX')!.value!,
        pointTwoModifiedY: this.form.get('pointTwo')!.get('newY')!.value!,
        pointTwoModifiedZ: this.form.get('pointTwo')!.get('newZ')!.value!,

        pointThreeOriginalX: this.pointThree.x,
        pointThreeOriginalY: this.pointThree.y,
        pointThreeOriginalZ: this.pointThree.z,

        pointThreeModifiedX: this.form.get('pointThree')!.get('newX')!.value!,
        pointThreeModifiedY: this.form.get('pointThree')!.get('newY')!.value!,
        pointThreeModifiedZ: this.form.get('pointThree')!.get('newZ')!.value!,
      }));

      // Clear current scene and reload
      this.scene!.clear();

      // Remove tags
      const viewerElement = this.viewer!.nativeElement;
      const tagElements = viewerElement.getElementsByClassName('tag-label');
      while (tagElements.length > 0) {
        viewerElement.removeChild(tagElements[0]);
      }

      this.loading = true;
      await this.loadModel(pointCloud);

      this.modifying = ModifyingStep.Success;
    } catch (error) {
      this.modifying = ModifyingStep.Error;
      console.error(error);
    } finally {
      this.loadingModification = false;
    }
  }

  close() {
    this.modifying = ModifyingStep.None;
    this.pointOne = null;
    this.pointTwo = null;
    this.form.get('pointOne')!.reset({ newX: 0, newY: 0, newZ: 0 });
    this.form.get('pointTwo')!.reset({ newX: 0, newY: 0, newZ: 0 });
    this.form.get('pointThree')!.reset({ newX: 0, newY: 0, newZ: 0 });
  }

  backStep() {
    if (this.modifying === ModifyingStep.pointTwo) {
      this.modifying = ModifyingStep.pointOne;
      this.pointTwo = null;
      this.form.get('pointTwo')!.reset({ newX: 0, newY: 0, newZ: 0 });
    } else if (this.modifying === ModifyingStep.pointThree) {
      this.modifying = ModifyingStep.pointTwo;
      this.pointThree = null;
      this.form.get('pointThree')!.reset({ newX: 0, newY: 0, newZ: 0 });
    }
  }

  get isSuccessful() {
    return this.modifying === ModifyingStep.Success;
  }

  get isError() {
    return this.modifying === ModifyingStep.Error;
  }

  async loadModel(pointCloud: PointCloud) {
    const blob = await firstValueFrom(this.pointCloudService.download(pointCloud));
    const url = URL.createObjectURL(blob);

    const loader = new PLYLoader(undefined, pointCloud.isTransformedToLatLng);
    loader.load(url, (geometry) => {
      const material = new THREE.PointsMaterial({size: 0.005});
      material.vertexColors = true;
      const mesh = new THREE.Points(geometry, material)
      this.scene!.add(mesh);

      // Position camera to fully view the point cloud
      const box = new THREE.Box3().setFromObject(mesh);
      const size = box.getSize(new THREE.Vector3()).length();
      const center = box.getCenter(new THREE.Vector3());

      this.controls!.reset();

      this.meshTranslation.x = (mesh.position.x - center.x);
      this.meshTranslation.y = (mesh.position.y - center.y);
      this.meshTranslation.z = (mesh.position.z - center.z);

      mesh.position.x += this.meshTranslation.x;
      mesh.position.y += this.meshTranslation.y;
      mesh.position.z += this.meshTranslation.z;

      this.controls!.maxDistance = size * 10;
      this.camera!.near = size / 100;
      this.camera!.far = size * 100;
      this.camera!.updateProjectionMatrix();

      this.camera!.position.copy(center);
      this.camera!.position.x += size / 2.0;
      this.camera!.position.y += size / 5.0;
      this.camera!.position.z += size / 2.0;
      this.camera!.lookAt(center);

      this.loading = false;
    });

    this.addTagLabels(pointCloud.tags);
  }
}
